import React, { useEffect, useState } from 'react'
import { Box, Button } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useSessionContext } from 'session'
import {
  ExecutivePositionFilters,
  type ExecutivePositionSearchFilters,
  initialExecutivePositionFilters,
  type TableEntity,
  UsTable,
  useExecOrgParentLuts,
  useGlobalMessageHandler,
  useUsPrefixLuts,
  useUsRoleTypeLuts
} from 'components'
import {
  type ExecOrgDescendantItem,
  type ExecutivePositionSearchResults,
  getExecutiveOrgDescendants,
  searchExecutivePositions
} from 'services'
import { EXEC_POSITION_STATUSES, STATE_EXECUTIVE_ROLES, suffixOptions } from 'utils'

const PAGE_SIZE = 50
const LINKED_ORG_NOT_FOUND = 'The linked organization was not found.'
const STATE_EXEC_ROLE_IDS = new Set<number>(STATE_EXECUTIVE_ROLES)

export const ExecutivePositionSearchPage: React.FC = () => {
  const { changeModuleName, changeUniqueId, openNewTab } = useSessionContext()
  const { lutValues: usPrefixLutValues } = useUsPrefixLuts()
  const { showErrorMessage } = useGlobalMessageHandler()
  const [searchParams] = useSearchParams()
  const { lutValues: parentLut, loading: parentLutLoading } = useExecOrgParentLuts()
  const { lutValues: roleLut, loading: roleLutLoading } = useUsRoleTypeLuts()

  useEffect(() => {
    changeModuleName('Executive Positions')
    changeUniqueId('')
  }, [changeModuleName, changeUniqueId])

  const [positions, setPositions] = useState<TableEntity[]>([])
  const [filters, setFilters] = useState<ExecutivePositionSearchFilters>(initialExecutivePositionFilters)
  const [lastQueriedFilters, setLastQueriedFilters] = useState<ExecutivePositionSearchFilters>(initialExecutivePositionFilters)
  const [loadResultWithFilters, setLoadResultWithFilters] = useState(false)
  const [pageCount, setPageCount] = useState<number>(0)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [loading, setLoading] = useState<boolean>(false)

  const parentOrgParam = Number(searchParams.get('parentOrg'))
  const childOrgParam = Number(searchParams.get('childOrg'))
  const [linkPending, setLinkPending] = useState<boolean>(parentOrgParam > 0)

  const defaultAdditionalColumns = [
    {
      Header: 'Prefix',
      accessor: 'person.prefixId',
      order_field: 'prefix',
      Cell: ({ value }: { value: number | null }) => usPrefixLutValues.prefixes.find(prefix => prefix.id === value)?.code ?? ''
    },
    { Header: 'First Name', accessor: 'person.firstName', order_field: 'first_name' },
    { Header: 'Last Name', accessor: 'person.lastName', order_field: 'last_name' },
    {
      Header: 'Suffix',
      accessor: 'person.suffixId',
      order_field: 'suffix',
      Cell: ({ value }: { value: number | null }) => suffixOptions.find(opt => opt.id === value)?.value ?? ''
    },
    { Header: 'Title', accessor: 'title', order_field: 'title' },
    {
      Header: 'Organization',
      accessor: 'org',
      order_field: 'org_name',
      Cell: ({ value }: { value: { name: string, parentOrg?: { name: string } | null } | null }) => {
        if (value == null) return ''
        return value.parentOrg?.name != null
          ? `${value.parentOrg.name} - ${value.name}`
          : value.name
      }
    },
    {
      Header: 'Status',
      accessor: 'statusId',
      order_field: 'status',
      Cell: ({ value }: { value: number | null }) => value != null ? (EXEC_POSITION_STATUSES[value]?.label ?? '') : ''
    },
    {
      Header: 'Org',
      accessor: 'org.id',
      order_field: '',
      Cell: ({ value }: { value: number | null }) => value != null && (
        <Button
          variant='outlined'
          size='small'
          sx={{ whiteSpace: 'nowrap' }}
          onClick={() => { openNewTab(`/us/executives/orgs/${value}`) }}
        >
          View Org
        </Button>
      )
    }
  ]

  const updateFilters = (updates: any): void => {
    setFilters(prevState => ({ ...prevState, ...updates }))
  }

  const cleanFilter = (): void => {
    const isPristine = JSON.stringify(initialExecutivePositionFilters) === JSON.stringify(lastQueriedFilters)
    setFilters(initialExecutivePositionFilters)
    if (!isPristine) {
      setLoadResultWithFilters(prevState => !prevState)
    }
  }

  const fetchPositions = async (): Promise<ExecutivePositionSearchResults> => {
    setLoading(true)

    const params: any = {
      'general_role[]': filters.generalRole != null
        ? (filters.generalRole.kind === 'state-executives'
            ? [...STATE_EXECUTIVE_ROLES]
            : [filters.generalRole.role.id])
        : undefined,
      state: filters.state != null ? filters.state.id : undefined,
      parent_org_id: filters.parentOrg != null ? filters.parentOrg.id : undefined,
      child_org_id: filters.childOrg != null ? filters.childOrg.id : undefined,
      exec_role_id: filters.execRoleId != null ? filters.execRoleId : undefined,
      vacant_only: filters.vacantOnly ? true : undefined,
      include_sub_offices: filters.includeSubOffices ? true : undefined,
      page: filters.page !== 0 ? filters.page : undefined
    }
    params.order_by = filters.order_field
    params.order_direction = filters.order_direction
    params.page_size = PAGE_SIZE
    return await searchExecutivePositions(params)
  }

  const updateTableWithFilter = async (): Promise<void> => {
    fetchPositions().then(response => {
      setPositions(response.data)
      setPageCount(response.total_pages)
      setTotalCount(response.total_items)
      setLastQueriedFilters(filters)
      if (filters.page !== response.page_number) {
        updateFilters({ page: response.page_number })
      }
    }).catch(() => {
      setPositions([])
      setPageCount(0)
      setTotalCount(0)
      showErrorMessage('Failed to load executive positions. Please try again.')
    }).finally(() => { setLoading(false) })
  }

  useEffect(() => {
    if (linkPending) return
    const updateTablePage = async (): Promise<void> => {
      await updateTableWithFilter()
    }
    void updateTablePage()
  }, [loadResultWithFilters])

  useEffect(() => {
    if (!linkPending || parentLutLoading || roleLutLoading) return

    const parentOrg = parentLut.parents.get(parentOrgParam)
    const role = parentOrg != null ? roleLut.roles.get(parentOrg.role.id) : undefined

    if (parentOrg == null || role == null) {
      showErrorMessage(LINKED_ORG_NOT_FOUND)
      setLinkPending(false)
      setLoadResultWithFilters(prevState => !prevState)
      return
    }

    const finish = (childOrg: ExecOrgDescendantItem | null): void => {
      setFilters({
        ...initialExecutivePositionFilters,
        generalRole: { kind: 'role', role },
        state: STATE_EXEC_ROLE_IDS.has(role.id) ? parentOrg.state : null,
        parentOrg,
        childOrg
      })
      setLinkPending(false)
      setLoadResultWithFilters(prevState => !prevState)
    }

    if (childOrgParam > 0) {
      void getExecutiveOrgDescendants(parentOrg.id)
        .then(descendants => {
          const childOrg = descendants.find(descendant => descendant.id === childOrgParam)
          if (childOrg == null) {
            showErrorMessage(LINKED_ORG_NOT_FOUND)
            finish(null)
            return
          }
          finish(childOrg)
        })
        .catch(() => {
          showErrorMessage('Failed to load the linked organization. Please try again.')
          finish(null)
        })
    } else {
      finish(null)
    }
  }, [linkPending, parentLutLoading, roleLutLoading])

  const triggerReload = (): void => {
    setLoadResultWithFilters(prevState => !prevState)
  }

  const openEditPosition = (entity: TableEntity): void => {
    entity.id != null
      ? openNewTab(`/us/executives/position-update/${entity.id}`)
      : showErrorMessage('No ID on this position.')
  }

  // Row-level Delete lives on the position update page and the org detail
  // page, not in the search results — see TI-3937 placement follow-up.
  // `openDelete` is required by `<UsTable>`'s prop shape but unused while
  // `showDelete` (default `false`) leaves the column unrendered.
  const noopDelete = (): void => {}

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Box width="97%" sx={{ pb: 1 }}>
        <ExecutivePositionFilters
          filters={filters}
          updateFilters={updateFilters}
          doFilter={updateTableWithFilter}
          cleanFilters={cleanFilter}
        />
        <UsTable
          entities={positions}
          filters={filters}
          updateFilters={updateFilters}
          triggerReload={triggerReload}
          pageCount={pageCount}
          totalCount={totalCount}
          additionalColumns={defaultAdditionalColumns}
          orderField={filters.order_field}
          orderDirection={filters.order_direction}
          loading={loading}
          openEdit={openEditPosition}
          openDelete={noopDelete}
        />
      </Box>
    </Box>
  )
}
